import { resolveApiOrigin } from '../../api/api-origin.js';
import { useRef, useState } from 'react';
import {
  useAssets,
  useDeleteAsset,
  useUploadAsset,
  inferMimeType,
  type Asset,
} from '../../api/assets.js';

/**
 * BrandAssetsPage — tenant-scoped library of logos and header images
 * authors can drop into report templates.
 *
 * The public URL (path-only, prefixed with `/reporting/api`) is what
 * goes into a template parameter like `logo_url`. We surface a
 * one-click "copy URL" button on every row so authors don't have
 * to compose it by hand.
 *
 * Hard limits enforced by the server:
 *   - 2 MB per asset
 *   - mime types: image/svg+xml, png, jpeg, webp, gif
 *
 * Phase 1 keeps the surface intentionally small — list, upload, copy,
 * delete. No tagging, no folders, no per-asset metadata. We can add
 * those once we know what people actually need.
 */
const MAX_BYTES = 2 * 1024 * 1024;

export function BrandAssetsPage(): JSX.Element {
  const { data: assets, isLoading, error } = useAssets();
  const upload = useUploadAsset();
  const remove = useDeleteAsset();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  async function handleFiles(files: FileList | null): Promise<void> {
    setUploadError(null);
    if (!files || files.length === 0) return;
    for (const file of Array.from(files)) {
      if (file.size > MAX_BYTES) {
        setUploadError(`${file.name} is ${(file.size / 1024 / 1024).toFixed(1)} MB; max is 2 MB.`);
        continue;
      }
      const bytes = await file.arrayBuffer();
      try {
        await upload.mutateAsync({
          name: file.name.replace(/\.[^.]+$/, ''),
          mimeType: inferMimeType(file),
          bytes,
        });
      } catch (err) {
        setUploadError(`Upload failed for ${file.name}: ${(err as Error).message}`);
      }
    }
    if (fileRef.current) fileRef.current.value = '';
  }

  async function handleCopy(asset: Asset): Promise<void> {
    // Compose the absolute URL the way authors will paste it. If the
    // SPA was built with VITE_API_BASE_URL we honor it; otherwise the
    // path-only URL works for same-origin renders.
    const apiOrigin = resolveApiOrigin().replace(/\/+$/u, '');
    const fullUrl = apiOrigin ? `${apiOrigin}${asset.url}/raw` : `${asset.url}/raw`;
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(asset.id);
      setTimeout(() => setCopied(null), 1600);
    } catch {
      setUploadError('Clipboard write blocked by the browser.');
    }
  }

  return (
    <>
      <header className="page-title">
        <div>
          <h1>Brand assets</h1>
          <p className="page-title-subtitle">
            Logos and header images, hosted by Sage and addressable via stable URLs. Drop the copied
            URL into a report-template parameter (e.g.
            <code> logo_url</code>) and the render engine will fetch it server-side. Max 2 MB; SVG /
            PNG / JPEG / WEBP / GIF.
          </p>
        </div>
      </header>

      <section
        className={`assets-dropzone${dragActive ? ' assets-dropzone--active' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragActive(false);
          void handleFiles(e.dataTransfer.files);
        }}
      >
        <div className="assets-dropzone-body">
          <p className="assets-dropzone-headline">
            Drag image files here, or
            <button type="button" className="link-btn" onClick={() => fileRef.current?.click()}>
              {' '}
              browse
            </button>
          </p>
          <p className="text-dim text-sm">
            We hash on upload and dedupe — re-uploading the same bytes returns the existing asset.
          </p>
          <input
            ref={fileRef}
            type="file"
            multiple
            accept="image/svg+xml,image/png,image/jpeg,image/webp,image/gif"
            style={{ display: 'none' }}
            onChange={(e) => void handleFiles(e.currentTarget.files)}
          />
        </div>
        {upload.isPending && <div className="assets-dropzone-status">Uploading…</div>}
      </section>

      {uploadError && (
        <div className="alert alert--danger" style={{ marginTop: 'var(--space-3)' }}>
          {uploadError}
        </div>
      )}

      <section style={{ marginTop: 'var(--space-4)' }}>
        {isLoading && <div className="text-dim">Loading…</div>}
        {error && <div className="alert alert--danger">Failed to load assets.</div>}
        {assets && assets.length === 0 && (
          <div className="text-dim">No brand assets yet. Upload your first logo above.</div>
        )}
        {assets && assets.length > 0 && (
          <div className="assets-grid">
            {assets.map((asset) => (
              <article key={asset.id} className="asset-card">
                <div className="asset-thumb">
                  <img src={`${asset.url}/raw`} alt={asset.name} />
                </div>
                <div className="asset-meta">
                  <p className="asset-name" title={asset.name}>
                    {asset.name}
                  </p>
                  <p className="text-dim text-xs">
                    {asset.mimeType} · {formatBytes(asset.sizeBytes)} ·{' '}
                    {formatDate(asset.createdAt)}
                  </p>
                  <div className="asset-actions">
                    <button
                      type="button"
                      className="btn btn--sm"
                      onClick={() => void handleCopy(asset)}
                    >
                      {copied === asset.id ? 'Copied' : 'Copy URL'}
                    </button>
                    <button
                      type="button"
                      className="btn btn--sm btn--ghost"
                      onClick={() => {
                        if (
                          window.confirm(
                            `Delete "${asset.name}"? Templates referencing this URL will break.`,
                          )
                        ) {
                          remove.mutate(asset.id);
                        }
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <BrandAssetsStyles />
    </>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return iso;
  return dt.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Page-scoped styles. We keep them inline so the page is self-contained
 *  and the global stylesheet doesn't have to grow for a one-off admin
 *  screen. Same pattern other admin pages use. */
function BrandAssetsStyles(): JSX.Element {
  return (
    <style>{`
      .assets-dropzone{
        margin-top:var(--space-3);
        padding:var(--space-5);
        border:1.5px dashed var(--color-border);
        border-radius:var(--radius-md);
        background:var(--color-surface-2);
        transition:border-color 0.12s, background 0.12s;
      }
      .assets-dropzone--active{
        border-color:var(--color-accent);
        background:var(--color-surface-1);
      }
      .assets-dropzone-body p{margin:0}
      .assets-dropzone-headline{font-weight:var(--font-weight-medium);margin-bottom:var(--space-1)}
      .assets-dropzone-status{margin-top:var(--space-2);font-size:var(--font-size-sm);color:var(--color-accent)}
      .link-btn{
        background:none;border:none;padding:0;color:var(--color-accent);cursor:pointer;text-decoration:underline;font:inherit;
      }
      .assets-grid{
        display:grid;
        grid-template-columns:repeat(auto-fill,minmax(220px,1fr));
        gap:var(--space-3);
      }
      .asset-card{
        border:1px solid var(--color-border-subtle);
        border-radius:var(--radius-md);
        background:var(--color-surface-1);
        overflow:hidden;
        display:flex;flex-direction:column;
      }
      .asset-thumb{
        aspect-ratio:16/9;
        background:repeating-conic-gradient(rgba(0,0,0,0.04) 0 25%, transparent 0 50%) 50% / 16px 16px;
        display:flex;align-items:center;justify-content:center;
        padding:var(--space-3);
      }
      .asset-thumb img{max-width:100%;max-height:100%;object-fit:contain}
      .asset-meta{padding:var(--space-3);display:flex;flex-direction:column;gap:var(--space-1)}
      .asset-name{
        margin:0;font-weight:var(--font-weight-medium);font-size:var(--font-size-sm);
        white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
      }
      .asset-actions{display:flex;gap:var(--space-2);margin-top:var(--space-2)}
      .btn{
        font:inherit;font-size:var(--font-size-xs);font-weight:var(--font-weight-semibold);
        padding:var(--space-1) var(--space-3);border-radius:var(--radius-sm);
        border:1px solid var(--color-border);background:var(--color-surface-1);color:var(--color-text-primary);
        cursor:pointer;
      }
      .btn:hover{background:var(--color-surface-2)}
      .btn--sm{padding:4px 10px}
      .btn--ghost{background:transparent;border-color:transparent;color:var(--color-text-tertiary)}
      .btn--ghost:hover{color:var(--color-danger,#b91c1c);background:var(--color-surface-2)}
    `}</style>
  );
}
